import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faImage } from '@fortawesome/free-solid-svg-icons';
import PageHeader from 'layout/PageHeader';
import WidgetSection from 'layout/WidgetSection';
import { useAuth } from 'common/context/AuthContext';
import { useInstance } from '../context/InstanceContext';
import {
	deleteInstanceLogo,
	uploadInstanceLogo,
} from '../services/instanceApi';
import InstanceMark from './InstanceMark';

const MAX_BYTES = 1048576;

const InstanceLogoPage: React.FC = () => {
	const navigate = useNavigate();
	const { isAdmin, permissionsLoaded } = useAuth();
	const { branding, reload } = useInstance();
	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const back = () => navigate('/app/settings/infrastructure');

	const handleUpload = async () => {
		const token = localStorage.getItem('token');
		if (!token || !file) return;
		if (
			file.type !== 'image/png' &&
			!file.name.toLowerCase().endsWith('.png')
		) {
			setError('Le fichier doit être un PNG.');
			return;
		}
		if (file.size > MAX_BYTES) {
			setError('Le fichier dépasse 1 Mo.');
			return;
		}
		setIsSubmitting(true);
		setError('');
		try {
			await uploadInstanceLogo(token, file);
			setFile(null);
			await reload();
		} catch (err) {
			console.error(err);
			setError(err instanceof Error ? err.message : 'Envoi impossible.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleReset = async () => {
		const token = localStorage.getItem('token');
		if (!token) return;
		setIsSubmitting(true);
		setError('');
		try {
			await deleteInstanceLogo(token);
			setFile(null);
			await reload();
		} catch (err) {
			console.error(err);
			setError(
				err instanceof Error ? err.message : 'Réinitialisation impossible.'
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (permissionsLoaded && !isAdmin) {
		return (
			<div className="min-h-screen bg-gray-100 p-6">
				<PageHeader
					icon={faImage}
					title="Logo de l'instance"
					subtitle="Réservé aux administrateurs"
				/>
				<div className="max-w-6xl mx-auto mt-6 flex justify-end">
					<button
						onClick={back}
						className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
					>
						<FontAwesomeIcon icon={faAngleLeft} />
						Retour
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-100 p-6">
			<PageHeader
				icon={faImage}
				title="Logo de l'instance"
				subtitle="PNG, 1 Mo maximum. Le même fichier est utilisé sur les PDF."
			/>

			<div className="max-w-6xl mx-auto mt-6 space-y-6">
				<WidgetSection>
					<div className="flex flex-col items-start gap-4">
						<div className="w-48 h-48 flex items-center justify-center bg-gray-50 rounded-xl border border-gray-200">
							<InstanceMark className="w-40 h-40 object-contain" />
						</div>
						<p className="text-sm text-gray-600">
							{branding?.hasLogo
								? 'Logo personnalisé de cette instance.'
								: "Logo Corelys par défaut. Il est remplacé dès qu'un PNG est enregistré."}
						</p>
						<input
							type="file"
							accept="image/png,.png"
							disabled={isSubmitting}
							onChange={(event) => {
								setFile(event.target.files?.[0] ?? null);
								setError('');
							}}
							className="text-sm text-gray-700"
						/>
						{error && <p className="text-sm text-red-600">{error}</p>}
						<div className="flex flex-wrap gap-3">
							<button
								type="button"
								onClick={() => void handleUpload()}
								disabled={!file || isSubmitting}
								className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:bg-indigo-300"
							>
								{isSubmitting ? 'Enregistrement…' : 'Enregistrer'}
							</button>
							{branding?.hasLogo && (
								<button
									type="button"
									onClick={() => void handleReset()}
									disabled={isSubmitting}
									className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
								>
									Revenir au logo par défaut
								</button>
							)}
						</div>
					</div>
				</WidgetSection>

				<div className="flex justify-end">
					<button
						onClick={back}
						className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
					>
						<FontAwesomeIcon icon={faAngleLeft} />
						Retour
					</button>
				</div>
			</div>
		</div>
	);
};

export default InstanceLogoPage;
